import { useEffect, useRef, useState, type MouseEvent } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  AudioLines,
  BookOpen,
  BookOpenText,
  Check,
  ChevronRight,
  CircleHelp,
  Download,
  ExternalLink,
  GraduationCap,
  Headphones,
  Leaf,
  LibraryBig,
  Menu,
  Orbit,
  ScanText,
  Search,
  Sparkles,
  X,
  ZoomIn,
} from "lucide-react";
import LibraryTool from "./components/LibraryTool";
import LibraryPage, {type LibraryPageConfig} from "./components/LibraryPage";
import LearningApps from "./components/LearningApps";
import CoursesPage from "./components/CoursesPage";
import SiteDialog from "./components/SiteDialog";
import {
  programmes,
  programmeFilters,
  type Programme,
  type ProgrammeFilter,
} from "./content/programmes";

const contactUrl = "https://tally.so/r/PddYY0";
const navItems = [
  ["Classes", "classes"],
  ["Courses", "courses"],
  ["Learning Apps", "learning-apps"],
  ["Granthālaya", "granthalaya"],
  ["Our work", "work"],
  ["Vidyākośa", "vidyakosha"],
  ["Heritage", "heritage"],
] as const;
const programmeIcons = {
  chant: AudioLines,
  consciousness: Orbit,
  leaf: Leaf,
  book: BookOpen,
  text: BookOpenText,
  sky: Sparkles,
};
const work = [
  {
    icon: ScanText,
    title: "Preserve a text",
    body: "Bring manuscripts and printed works into carefully reviewed digital editions.",
  },
  {
    icon: BookOpenText,
    title: "Edit & publish",
    body: "Make trusted texts available as beautiful books and useful reading editions.",
  },
  {
    icon: GraduationCap,
    title: "Teach & learn",
    body: "Connect traditional study with courses, conversations and learning resources.",
  },
  {
    icon: Search,
    title: "Research & discover",
    body: "Follow a question through original passages, commentary and context.",
  },
];
const possibilities = [
  {
    id: "books",
    label: "Publish books",
    icon: BookOpenText,
    title: "From source page to reading edition.",
    text: "Correct a digitised text, review its structure, and prepare an edition for reading and publication.",
    outcome: "A reviewed book, ready to share",
    steps: ["Preserve the source", "Review and edit", "Create the edition"],
  },
  {
    id: "courses",
    label: "Create courses",
    icon: GraduationCap,
    title: "Give knowledge a path to the learner.",
    text: "The Vidyākośa vision brings texts and teaching together: shape readings into lessons, study guides and thoughtful courses.",
    outcome: "A course grounded in source texts",
    steps: ["Choose the texts", "Shape the lessons", "Guide the learner"],
  },
  {
    id: "research",
    label: "Explore ideas",
    icon: Search,
    title: "Follow the idea. Return to the source.",
    text: "Explore a topic or question across a collection, with original passages and references at the heart of the research.",
    outcome: "Research with traceable references",
    steps: ["Ask a question", "Explore passages", "Study the sources"],
  },
  {
    id: "audio",
    label: "Make audio",
    icon: Headphones,
    title: "Let a reviewed text be heard.",
    text: "Prepare listening editions from approved text, with a human ear for pronunciation, pacing and the character of the work.",
    outcome: "A listening edition for further review",
    steps: ["Approve the text", "Prepare the reading", "Listen and refine"],
  },
] as const;

function App({sharedDemo=false,page,learningPage=false,coursesPage=false}:{sharedDemo?:boolean;page?:LibraryPageConfig;learningPage?:boolean;coursesPage?:boolean}={}) {
  const sectionHref=(id:string)=>id==="main"?"#main":id==="courses"?"/courses/":id==="learning-apps"?"/learning-apps/":sharedDemo&&id==="granthalaya"?"/tantra-granthalaya/catalogue/":page||learningPage||coursesPage?`/#${id}`:`#${id}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("top");
  const [filter, setFilter] = useState<ProgrammeFilter>("All programmes");
  const [programme, setProgramme] = useState<Programme | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [possibility, setPossibility] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [copyrightYear, setCopyrightYear] = useState(2026);
  const menuRef = useRef<HTMLButtonElement>(null);
  const openEnquiry = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    // The mobile menu closes behind the dialog; return focus to its visible toggle.
    if (menuOpen) { menuRef.current?.focus(); setMenuOpen(false); }
    setContactOpen(true);
  };
  const visibleProgrammes = programmes.filter(
    (item) => filter === "All programmes" || item.category === filter,
  );
  const selectedPossibility = possibilities[possibility];

  useEffect(() => setCopyrightYear(new Date().getFullYear()), []);

  useEffect(() => {
    const closeMenu = () => setMenuOpen(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        menuRef.current?.focus();
      }
    };
    window.addEventListener("hashchange", closeMenu);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("hashchange", closeMenu);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 1280px)");
    const closeForDesktop = () => {
      if (media.matches) setMenuOpen(false);
    };
    media.addEventListener("change", closeForDesktop);
    return () => media.removeEventListener("change", closeForDesktop);
  }, []);
  useEffect(() => {
    let observer: IntersectionObserver;
    const observeSections = () => {
      observer?.disconnect();
      const height = window.innerHeight;
      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting);
          if (visible.length) setActiveSection(visible[0].target.id);
        },
        {
          // A thin band avoids selecting two adjacent sections at the same time.
          rootMargin: `-${Math.round(height * 0.3)}px 0px -${Math.max(0, Math.round(height * 0.7) - 2)}px 0px`,
          threshold: 0,
        },
      );
      document
        .querySelectorAll("main > section[id]")
        .forEach((section) => observer.observe(section));
    };
    observeSections();
    window.addEventListener("resize", observeSections);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", observeSections);
    };
  }, []);

  return (
    <div className="site-shell">
      <a className="skip-link" href={sectionHref("main")} >
        Skip to content
      </a>
      <div className="institution-strip">
        <div className="container strip-inner">
          <span>
            Established 1944 <span aria-hidden="true">·</span> Registered 1949
          </span>
          <span>
            Preservation <i /> Scholarship <i /> Learning
          </span>
        </div>
      </div>
      <header className="site-header">
        <div className="container header-inner">
          <a
            className="brand"
            href={sectionHref("top")}
            aria-label="The Bharatheeya Institute home"
            onClick={() => setMenuOpen(false)}
          >
            <img
              src="/brand/bivr-emblem.png"
              width="62"
              height="62"
              alt=""
              fetchPriority="high"
            />
            <span>
              <strong>
                The Bharatheeya Institute<sup>®</sup>
              </strong>
              <small>of Vedic Research</small>
            </span>
          </a>
          <button
            ref={menuRef}
            className="icon-button menu-button"
            type="button"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-controls="main-navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
          <nav
            id="main-navigation"
            className={menuOpen ? "main-nav is-open" : "main-nav"}
            aria-label="Main navigation"
          >
            {navItems.map(([label, id]) => (
              <a
                key={id}
                href={sectionHref(id)}
                aria-current={coursesPage && id === "courses" ? "page" : activeSection === id ? "location" : undefined}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </a>
            ))}
            <a
              className="nav-contact"
              href={contactUrl}
              onClick={openEnquiry}
            >
              Get in touch <ArrowUpRight size={15} aria-hidden="true" />
            </a>
          </nav>
        </div>
      </header>
      {menuOpen && (
        <button
          className="nav-scrim"
          aria-label="Dismiss navigation"
          tabIndex={-1}
          onClick={() => {
            setMenuOpen(false);
            menuRef.current?.focus();
          }}
        />
      )}
      <main id="main" tabIndex={-1}>
        {coursesPage?<CoursesPage onEnquire={openEnquiry}/>:learningPage?<LearningApps page onEnquire={openEnquiry}/>:page?<LibraryPage config={page}/>:<>
        <section className="hero" id="top" aria-labelledby="hero-title">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">
                <span className="eyebrow-line" /> A living tradition of enquiry
              </p>
              <h1 id="hero-title">
                Ancient wisdom.
                <br />
                <em>A living future.</em>
              </h1>
              <p className="hero-lead">
                A home for the study, preservation and sharing of India’s Vedic
                and śāstric knowledge. Rooted in tradition. Open to discovery.
              </p>
              <div className="action-row">
                <a className="button primary" href={sectionHref("classes")} >
                  Find your study circle{" "}
                  <ArrowUpRight size={18} aria-hidden="true" />
                </a>
                <a className="text-link" href={sectionHref("granthalaya")} >
                  Discover Granthālaya{" "}
                  <ArrowRight size={17} aria-hidden="true" />
                </a>
              </div>
              <div className="hero-footnote">
                <span className="mini-rule" /> Preserving knowledge. Enabling
                learning. Since 1944.
              </div>
            </div>
            <div className="hero-identity">
              <div className="identity-art">
                <div className="identity-arch" aria-hidden="true" />
                <div className="identity-orbit" aria-hidden="true" />
                <span className="identity-overline">
                  The Bharatheeya Institute<sup>®</sup>
                </span>
                <img
                  src="/brand/bivr-emblem.png"
                  width="380"
                  height="380"
                  alt="The Institute’s emblem: the cow and lion beneath the flag"
                  fetchPriority="high"
                />
                <p lang="sa">विद्या · साधना · अनुसन्धानम्</p>
                <span className="identity-translation">
                  Learning · Practice · Enquiry
                </span>
              </div>
              <div className="identity-caption">
                <span>
                  <span className="caption-dot" /> A tradition carried forward
                </span>
                <a href={sectionHref("heritage")} >
                  Our story <ArrowDown size={15} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </section>
        <div className="pathways container" aria-label="Explore the Institute">
          <a href={sectionHref("classes")} >
            <span className="pathway-number">01</span>
            <div>
              <strong>Come to learn</strong>
              <span>Classes & study circles</span>
            </div>
            <ArrowUpRight aria-hidden="true" />
          </a>
          <a href={sectionHref("granthalaya")} >
            <span className="pathway-number">02</span>
            <div>
              <strong>Come to discover</strong>
              <span>Texts & living knowledge</span>
            </div>
            <ArrowUpRight aria-hidden="true" />
          </a>
          <a href={sectionHref("contact")} >
            <span className="pathway-number">03</span>
            <div>
              <strong>Come to contribute</strong>
              <span>Research & collaboration</span>
            </div>
            <ArrowUpRight aria-hidden="true" />
          </a>
        </div>
        <section
          className="section programmes-section"
          id="classes"
          aria-labelledby="classes-title"
        >
          <div className="container">
            <div className="section-intro">
              <div>
                <p className="eyebrow">Study at the Institute</p>
                <h2 id="classes-title">
                  A place to learn.
                  <br />
                  <em>A space to understand.</em>
                </h2>
              </div>
              <div className="section-aside">
                <p>
                  Small circles. Thoughtful questions. Close attention to the
                  text. Explore classes that bring study and practice together.
                </p>
                <span className="quiet-label">
                  Online & in person · Programmes vary by term
                </span>
              </div>
            </div>
            <div className="programme-controls">
              <div
                className="filter-group"
                role="group"
                aria-label="Filter programmes"
              >
                {programmeFilters.map((item) => (
                  <button
                    key={item}
                    type="button"
                    aria-pressed={filter === item}
                    onClick={() => setFilter(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <span className="result-count" role="status">
                {visibleProgrammes.length} programmes
              </span>
            </div>
            <div className="programme-grid">
              {visibleProgrammes.map((item) => {
                const Icon = programmeIcons[item.icon];
                return (
                  <article
                    className={`programme-card tone-${item.tone}`}
                    key={item.id}
                  >
                    <div className="programme-art">
                      <span className="programme-sanskrit" lang="sa">
                        {item.sanskrit}
                      </span>
                      <div className="programme-art-lines" aria-hidden="true" />
                      <Icon
                        className="programme-symbol"
                        size={40}
                        strokeWidth={1.2}
                        aria-hidden="true"
                      />
                      <span className="programme-kind">{item.kind}</span>
                    </div>
                    <div className="programme-body">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <button
                        className="programme-open"
                        type="button"
                        onClick={() => setProgramme(item)}
                        aria-label={`View ${item.title} programme`}
                      >
                        View programme{" "}
                        <span>
                          <ArrowUpRight size={18} aria-hidden="true" />
                        </span>
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
            <p className="section-note">
              <CircleHelp size={16} aria-hidden="true" /> New to a subject?{" "}
              <a href={sectionHref("contact")} >We’ll help you find a starting point.</a>
            </p>
          </div>
        </section>
        <LearningApps onEnquire={openEnquiry}/>
        <section
          className="section library-section"
          id="granthalaya"
          aria-labelledby="library-title"
        >
          <div className="container library-grid">
            <div className="library-copy">
              <p className="eyebrow">The Bharatheeya Institute Granthālaya</p>
              <h2 id="library-title">
                Old texts.
                <br />
                <em>New possibilities.</em>
              </h2>
              <p className="section-lead">
                A growing library for works that deserve to be preserved, read
                and understood.
              </p>
              <p>
                Granthālaya brings original sources, reviewed text, reading
                editions and audio into one place—so a page can become a
                starting point for discovery.
              </p>
              <div className="library-features">
                <span>
                  <BookOpenText size={17} /> Reviewed editions
                </span>
                <span>
                  <Search size={17} /> Text & meaning
                </span>
                <span>
                  <Headphones size={17} /> Listening editions
                </span>
              </div>
              <a className="button primary" href={sectionHref("contact")} >
                Enquire about the collection{" "}
                <ArrowUpRight size={18} aria-hidden="true" />
              </a>
              <span className="library-caption">
                Our digital preservation programme · In development
              </span>
            </div>
            <div className="library-showcase">
              <LibraryTool />
            </div>
          </div>
          <div className="container library-bottom">
            <span lang="sa">ग्रन्थालयः</span>
            <p>
              A source preserved. A text understood. A tradition carried
              forward.
            </p>
            <LibraryBig size={23} aria-hidden="true" />
          </div>
        </section>
        <section
          className="section work-section"
          id="work"
          aria-labelledby="work-title"
        >
          <div className="container">
            <div className="section-intro compact">
              <div>
                <p className="eyebrow">The work that connects us</p>
                <h2 id="work-title">
                  Knowledge, <em>put into practice.</em>
                </h2>
              </div>
              <a className="text-link" href={sectionHref("contact")} >
                Take part in our work{" "}
                <ArrowUpRight size={18} aria-hidden="true" />
              </a>
            </div>
            <div className="work-grid">
              {work.map(({ icon: Icon, title, body }, index) => (
                <article className="work-item" key={title}>
                  <div className="work-item-top">
                    <Icon size={27} strokeWidth={1.35} aria-hidden="true" />
                    <span>0{index + 1}</span>
                  </div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section
          className="section vision-section"
          id="vidyakosha"
          aria-labelledby="vision-title"
        >
          <div className="container vision-panel">
            <div className="vision-copy">
              <p className="eyebrow light">Meet Vidyākośa</p>
              <h2 id="vision-title">
                More ways
                <br />
                to bring knowledge
                <br />
                <em>to life.</em>
              </h2>
              <p>
                Our vision for an AI-assisted workspace where teachers, editors
                and researchers can turn trusted texts into books, courses,
                research and audio.
              </p>
              <a
                className="text-link light"
                href="/documents/BIVR_Vidyakosha_Vision.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Read the two-page vision{" "}
                <ArrowUpRight size={18} aria-hidden="true" />
                <span className="file-type">PDF</span>
              </a>
              <div className="vision-ownership">
                <span className="caption-dot" /> Built around BIVR’s own
                knowledge and local AI.
              </div>
            </div>
            <div className="vision-workspace">
              <div className="vision-toolbar">
                <span className="workspace-mark">
                  <img
                    src="/brand/bivr-emblem.png"
                    width="28"
                    height="28"
                    alt=""
                  />{" "}
                  Vidyākośa
                </span>
                <span className="workspace-label">Explore the vision</span>
              </div>
              <div
                className="vision-options"
                role="group"
                aria-label="Explore Vidyakosha possibilities"
              >
                {possibilities.map(({ id, label, icon: Icon }, index) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={possibility === index}
                    onClick={() => setPossibility(index)}
                  >
                    <Icon size={17} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
              <div className="vision-detail" aria-live="polite">
                <span className="vision-detail-icon">
                  <selectedPossibility.icon
                    size={26}
                    strokeWidth={1.4}
                    aria-hidden="true"
                  />
                </span>
                <h3>{selectedPossibility.title}</h3>
                <p>{selectedPossibility.text}</p>
                <ol className="vision-steps">
                  {selectedPossibility.steps.map((step, index) => (
                    <li key={step}>
                      <span>0{index + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
                <div className="vision-outcome">
                  <Check size={17} aria-hidden="true" />
                  {selectedPossibility.outcome}
                </div>
              </div>
            </div>
          </div>
        </section>
        <section
          className="section heritage-section"
          id="heritage"
          aria-labelledby="heritage-title"
        >
          <div className="container heritage-grid">
            <figure className="archive-card">
              <a
                href="/archive/founding-deed-detail.jpg"
                className="archive-image-button"
                onClick={(event) => {
                  event.preventDefault();
                  setArchiveOpen(true);
                }}
                aria-label="View the complete first page of the 1949 registration record"
              >
                <img
                  src="/archive/founding-deed-detail.jpg"
                  width="1007"
                  height="1521"
                  loading="lazy"
                  decoding="async"
                  alt="Complete first page of the Institute’s 1949 rules and regulations, including the stamp paper, names and signatures"
                />
                <span className="archive-zoom">
                  <ZoomIn size={17} aria-hidden="true" /> View full page
                </span>
              </a>
              <figcaption>
                <span>From the Institute’s archive</span>
                <strong>Rules & Regulations · Madras, 1949</strong>
              </figcaption>
            </figure>
            <div className="heritage-copy">
              <p className="eyebrow">Our roots, our responsibility</p>
              <h2 id="heritage-title">
                A vision from 1944.
                <br />
                <em>Still unfolding.</em>
              </h2>
              <p className="section-lead">
                The Bharatheeya Institute was established by Pujyaśrī Devinatha
                Gurudeva to renew Vedic and śāstric learning.
              </p>
              <p>
                Formally registered in Madras in 1949, the Institute continues
                that work through study, scholarship and preservation. This
                first page of the original record is a living connection to our
                beginnings.
              </p>
              <div className="heritage-timeline">
                <div>
                  <strong>1944</strong>
                  <span>The Institute established</span>
                </div>
                <div>
                  <strong>1949</strong>
                  <span>Formally registered in Madras</span>
                </div>
                <div>
                  <strong>Today</strong>
                  <span>A new chapter of learning</span>
                </div>
              </div>
              <a
                className="text-link"
                href="https://sridevinatha.org"
                target="_blank"
                rel="noreferrer"
              >
                Discover Sri Devinatha Mission{" "}
                <ArrowUpRight size={17} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>
        <section
          className="section contact-section"
          id="contact"
          aria-labelledby="contact-title"
        >
          <div className="container">
            <div className="contact-panel">
              <div>
                <p className="eyebrow light">There is a place for you here</p>
                <h2 id="contact-title">
                  Bring your curiosity.
                  <br />
                  <em>Let’s begin a conversation.</em>
                </h2>
                <p>
                  Join a study circle, share a manuscript, explore a research
                  idea, or offer your time and skills.
                </p>
              </div>
              <div className="contact-actions">
                <a
                  className="button gold"
                  href={contactUrl}
                  onClick={openEnquiry}
                >
                  Write to the Institute{" "}
                  <ChevronRight size={18} aria-hidden="true" />
                </a>
                <span>Classes · Research · Volunteering · Collaboration</span>
              </div>
            </div>
          </div>
        </section>
        </>}
      </main>
      <footer className="site-footer">
        <div className="container">
          <div className="footer-main">
            <div className="footer-identity">
              <a className="brand" href={sectionHref("top")} >
                <img
                  src="/brand/bivr-emblem.png"
                  width="58"
                  height="58"
                  alt=""
                  loading="lazy"
                />
                <span>
                  <strong>
                    The Bharatheeya Institute<sup>®</sup>
                  </strong>
                  <small>of Vedic Research</small>
                </span>
              </a>
              <p>
                Preserving knowledge.
                <br />
                Enabling learning.
              </p>
            </div>
            <div className="footer-link-group">
              <strong>Explore</strong>
              <a href={sectionHref("classes")} >Classes & study circles</a>
              <a href="/courses/">Courses</a>
              <a href="/learning-apps/">Learning Apps</a>
              <a href={sectionHref("granthalaya")} >Granthālaya</a>
              <a href={sectionHref("vidyakosha")} >Vidyākośa</a>
            </div>
            <div className="footer-link-group">
              <strong>The Institute</strong>
              <a href={sectionHref("heritage")} >Our heritage</a>
              <a
                href="https://sridevinatha.org"
                target="_blank"
                rel="noreferrer"
              >
                Sri Devinatha Mission{" "}
                <ArrowUpRight size={13} aria-hidden="true" />
              </a>
              <a
                href="https://sridevinathamission.substack.com/archive"
                target="_blank"
                rel="noreferrer"
              >
                Articles & reflections{" "}
                <ArrowUpRight size={13} aria-hidden="true" />
              </a>
            </div>
            <a className="back-to-top" href={sectionHref("top")}  aria-label="Back to top">
              <ArrowUp size={20} />
            </a>
          </div>
          <div className="footer-bottom">
            <span>
              © {copyrightYear} The Bharatheeya Institute® of Vedic Research
            </span>
            <span>
              Powered by <strong>AIEUN Labs®</strong>
            </span>
          </div>
        </div>
      </footer>
      {programme && (
        <SiteDialog
          titleId="programme-dialog-title"
          onClose={() => setProgramme(null)}
        >
          <div className={`programme-dialog-heading tone-${programme.tone}`}>
            <span className="eyebrow">{programme.kind}</span>
            <span className="dialog-sanskrit" lang="sa">
              {programme.sanskrit}
            </span>
            <h2 id="programme-dialog-title">{programme.title}</h2>
          </div>
          <div className="programme-dialog-content">
            <p>{programme.detail}</p>
            <h3>What we explore</h3>
            <ul>
              {programme.topics.map((topic) => (
                <li key={topic}>
                  <Check size={17} aria-hidden="true" />
                  <span>{topic}</span>
                </li>
              ))}
            </ul>
            <p className="programme-schedule">
              Contact the Institute for the next term’s dates, teaching format
              and joining details.
            </p>
            <a
              className="button primary"
              href={contactUrl}
              onClick={openEnquiry}
            >
              Enquire about this programme{" "}
              <ArrowUpRight size={18} aria-hidden="true" />
            </a>
            <small>Mention {programme.title} in your message.</small>
          </div>
        </SiteDialog>
      )}
      {contactOpen && (
        <SiteDialog
          titleId="contact-dialog-title"
          className="contact-dialog"
          onClose={() => setContactOpen(false)}
        >
          <div className="contact-dialog-heading">
            <p className="eyebrow">Write to the Institute</p>
            <h2 id="contact-dialog-title">Begin a conversation</h2>
            <p>
              Classes, research, manuscripts, volunteering and collaboration.
            </p>
          </div>
          <div className="contact-dialog-form">
            <iframe
              src="https://tally.so/embed/PddYY0?alignLeft=1&hideTitle=1&transparentBackground=1"
              title="Enquiry form for The Bharatheeya Institute"
            />
            <div className="contact-dialog-fallback">
              <span>Having trouble with the form?</span>
              <a
                className="text-link"
                href={contactUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open it separately <ExternalLink size={15} aria-hidden="true" />
              </a>
            </div>
          </div>
        </SiteDialog>
      )}
      {archiveOpen && (
        <SiteDialog
          titleId="archive-dialog-title"
          className="archive-dialog"
          onClose={() => setArchiveOpen(false)}
        >
          <div className="archive-dialog-heading">
            <p className="eyebrow">From the archive</p>
            <h2 id="archive-dialog-title">The 1949 registration record</h2>
            <a
              className="text-link"
              href="/archive/founding-deed-detail.jpg"
              download="BIVR-registration-record-1949-page-1.jpg"
            >
              Download first page <Download size={16} aria-hidden="true" />
            </a>
          </div>
          <img
            src="/archive/founding-deed-detail.jpg"
            width="1007"
            height="1521"
            alt="Complete first page of the Institute’s 1949 registration-era record"
          />
        </SiteDialog>
      )}
    </div>
  );
}
export default App;
